/**
 * Wallet — USDT кошелёк (read-only view until payment integration ships).
 *
 * Показывает:
 *   1. Общий USDT баланс + дневной лимит 4%
 *   2. Переключатель табов: History · P2P · Withdraw
 *   3. History: список всех финансовых событий (claim, referral,
 *      deposit, withdrawal, p2p) — из luna_balance_log
 *   4. P2P: форма отправки другому юзеру (API готов /p2p/send)
 *   5. Withdraw: UI-заготовка, показывает лимит и историю заявок —
 *      платёжка/действия подключаются в самом конце (задача отдельная)
 *
 * Депозит здесь отсутствует по решению админа — делаем платёжку в
 * последнюю очередь. Пока wallet это информативная страница + P2P.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { motion, type Variants, AnimatePresence } from 'motion/react';
import { useTranslation, Trans } from 'react-i18next';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  History,
  Send,
  ArrowUpRight,
  TrendingUp,
  Sparkles,
  Trophy,
} from 'lucide-react';
import {
  GlassSurface,
  LiquidNumber,
  GlassPill,
  GlassButton,
  ClickableCard,
  InfoTooltip,
} from '@/components/glass';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import { DepositCard } from '@/components/payment/DepositCard';
import { WithdrawCard } from '@/components/payment/WithdrawCard';
import { ProfileHeader } from '@/components/profile/ProfileHeader';

// ────────────────────────────────────────────────────────────────────
// Mock data — real data from GET /auth/me + /withdraw/limit + history
// ────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────
// Tab type
// ────────────────────────────────────────────────────────────────────
type TabKey = 'history' | 'p2p' | 'withdraw' | 'deposit';

// ────────────────────────────────────────────────────────────────────
// Animation
// ────────────────────────────────────────────────────────────────────
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { delayChildren: 0.05, staggerChildren: 0.06 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.32, 0.72, 0, 1] },
  },
};

// ────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────
export function Wallet() {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const [tab, setTab] = useState<TabKey>('history');
  const { user, refresh: refreshAuth } = useAuth();
  // Реальный USDT-баланс — единый источник с хедером (AuthContext).
  // Обновляется сразу после refreshAuth() (пополнение/вывод/test-credit),
  // не ждёт 30-секундный полинг useDashboardUser.
  const realBalance = parseFloat(user?.usdtBalance ?? '0');

  // Дневной лимит вывода — реальный из /api/luna/withdraw/limit
  const [dailyLimit, setDailyLimit] = useState<{ used: number; limit: number }>(
    { used: 0, limit: 0 },
  );
  useEffect(() => {
    import('@/services/api').then(({ lunaApi }) => {
      lunaApi.withdraw
        .limit()
        .then((res) => {
          const d = res.data as {
            success: boolean;
            usedToday?: string;
            dailyLimit?: string;
          };
          if (d.success) {
            setDailyLimit({
              used: parseFloat(d.usedToday ?? '0'),
              limit: parseFloat(d.dailyLimit ?? '0'),
            });
          }
        })
        .catch(() => { /* фид без лимита если API недоступно */ });
    });
  }, []);

  const dailyPct = useMemo(
    () =>
      dailyLimit.limit > 0
        ? Math.min(100, (dailyLimit.used / dailyLimit.limit) * 100)
        : 0,
    [dailyLimit],
  );

  const handleTab = (next: TabKey) => {
    // Все табы кошелька — платёжные. Один и тот же payment-тап звук,
    // чтобы у раздела была цельная акустическая идентичность.
    uiSounds.paymentTap();
    haptic.selection();
    setTab(next);
  };

  return (
    <motion.div
      className="space-y-5 max-w-md mx-auto pt-2 pb-6"
      variants={containerVariants}
      initial="hidden"
      animate="show"
      // Вся страница кошелька — платёжная зона. Глобальный uiTap
      // здесь выключен, чтобы играл только payment_tap.mp3 (Glass
      // tick) без двойного звукового слоя.
      data-no-sound="true"
    >
      {/* ─── Профиль ─── */}
      <motion.div variants={itemVariants}>
        <ProfileHeader />
      </motion.div>

      {/* ─── HERO balance ─── */}
      <motion.div variants={itemVariants}>
        <GlassSurface variant="strong" className="p-6 relative overflow-hidden">
          {/* Radial glow */}
          <div
            aria-hidden
            className="absolute pointer-events-none"
            style={{
              top: -60,
              right: -60,
              width: 240,
              height: 240,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, color-mix(in oklab, var(--accent-secondary) 40%, transparent) 0%, transparent 65%)',
              opacity: 0.6,
            }}
          />

          <div className="relative">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1 font-mono">
              {t('wallet.kicker')}
            </p>
            <h1 className="font-display text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight mb-4">
              {t('wallet.balanceTitle')}
            </h1>

            <div className="flex items-baseline gap-2 mb-5">
              <LiquidNumber
                value={realBalance}
                decimals={2}
                suffix="USD₮"
                className="text-[38px] font-extrabold font-display text-[var(--text-primary)] tracking-tight"
              />
            </div>

            {/* Daily limit card */}
            <div
              className="rounded-2xl p-3.5 relative"
              style={{
                background: 'var(--glass-fill)',
                border: '1px solid var(--glass-stroke)',
              }}
            >
              <InfoTooltip
                tone="lunar"
                className="!top-[6px] !right-[6px] !h-5 !w-5"
                ariaLabel={t('wallet.limitTooltipAria')}
                title={t('wallet.limitTooltipTitle')}
                body={
                  <Trans
                    i18nKey="wallet.limitTooltipBody"
                    components={{
                      b: <strong />,
                      br: <br />,
                      c: (
                        <code style={{
                          display: 'block',
                          padding: '8px 10px',
                          marginTop: 6,
                          borderRadius: 8,
                          background: 'rgba(255,255,255,0.05)',
                          fontSize: 12,
                          color: 'var(--accent-primary)',
                        }} />
                      ),
                    }}
                  />
                }
                bullets={[
                  t('wallet.limitBullet1'),
                  t('wallet.limitBullet2'),
                  t('wallet.limitBullet3'),
                  t('wallet.limitBullet4'),
                  t('wallet.limitBullet5'),
                  t('wallet.limitBullet6'),
                ]}
              />
              <div className="flex items-center justify-between mb-1.5 pr-7">
                <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-mono font-bold">
                  {t('wallet.dailyLimit')}
                </span>
                <span className="font-mono text-[11px] font-extrabold text-[var(--text-primary)] tabular-nums">
                  {t('wallet.limitUsedOf', { used: dailyLimit.used.toFixed(0), limit: dailyLimit.limit.toFixed(0) })}
                </span>
              </div>
              <div
                className="h-[5px] rounded-full overflow-hidden"
                style={{ background: 'var(--glass-stroke)' }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${dailyPct}%`,
                    background:
                      'linear-gradient(90deg, var(--accent-primary), var(--accent-secondary))',
                    boxShadow:
                      '0 0 12px color-mix(in oklab, var(--accent-primary) 60%, transparent)',
                  }}
                />
              </div>
              <p className="text-[9px] font-mono text-[var(--text-muted)] mt-1 uppercase tracking-[0.14em]">
                {t('wallet.limitHint')}
              </p>
            </div>

            {/* ─── Действия: пополнить / вывести ─── */}
            {/* В test-режиме быстрый «Пополнить» моментально кредитит 1000
                USDT; в production — открывает таб «Пополнить» с реальным
                flow (выбор сети, QR, адрес). Бэкенд сам определяет режим. */}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                type="button"
                onClick={async () => {
                  uiSounds.paymentTap();
                  haptic.medium();
                  try {
                    const { lunaApi } = await import('@/services/api');
                    const resp = await lunaApi.deposit.wallet('TON');
                    const data = resp.data;
                    if (data.mode === 'test') {
                      // test — бэк моментально зачислил 1000 USDT.
                      // Обновляем баланс БЕЗ reload чтобы онбординг
                      // не перезапускался. History и Dashboard ловят
                      // 'luna:deposit-done' и тихо подтягивают данные.
                      void refreshAuth();
                      window.dispatchEvent(
                        new CustomEvent('luna:deposit-done'),
                      );
                    } else {
                      // production — открываем полный таб «Пополнить»
                      // с QR и адресом. DepositCard сам подхватит
                      // уже созданный кошелёк через /deposit/wallet.
                      setTab('deposit');
                    }
                  } catch (err) {
                    const msg = (err as { response?: { data?: { message?: string } } })
                      ?.response?.data?.message ?? t('wallet.depositFailed');
                    alert(msg);
                  }
                }}
                className="rounded-2xl py-3 flex items-center justify-center gap-2 text-[13px] font-extrabold transition active:scale-[0.97]"
                style={{
                  background:
                    'linear-gradient(180deg, color-mix(in oklab, var(--status-positive) 22%, transparent), color-mix(in oklab, var(--status-positive) 10%, transparent))',
                  border: '1px solid color-mix(in oklab, var(--status-positive) 35%, transparent)',
                  color: 'var(--status-positive)',
                  boxShadow:
                    'inset 0 1px 0 0 rgba(255,255,255,0.1), 0 4px 12px -4px color-mix(in oklab, var(--status-positive) 30%, transparent)',
                }}
              >
                <ArrowDownCircle size={16} strokeWidth={2.2} />
                {t('wallet.depositBtn')}
              </button>
              <button
                type="button"
                onClick={() => {
                  uiSounds.paymentTap();
                  haptic.medium();
                  setTab('withdraw');
                }}
                className="rounded-2xl py-3 flex items-center justify-center gap-2 text-[13px] font-extrabold transition active:scale-[0.97]"
                style={{
                  background:
                    'linear-gradient(180deg, color-mix(in oklab, var(--accent-primary) 22%, transparent), color-mix(in oklab, var(--accent-primary) 10%, transparent))',
                  border: '1px solid color-mix(in oklab, var(--accent-primary) 35%, transparent)',
                  color: 'var(--accent-primary)',
                  boxShadow:
                    'inset 0 1px 0 0 rgba(255,255,255,0.1), 0 4px 12px -4px color-mix(in oklab, var(--accent-primary) 30%, transparent)',
                }}
              >
                <ArrowUpRight size={16} strokeWidth={2.2} />
                {t('wallet.withdrawBtn')}
              </button>
            </div>
          </div>
        </GlassSurface>
      </motion.div>

      {/* ─── TAB SWITCHER + CONTENT ─── */}
      <motion.div variants={itemVariants} className="space-y-3">
        <div
          className="rounded-2xl p-1 grid grid-cols-4 gap-1"
          style={{
            background: 'var(--glass-fill)',
          }}
        >
          <TabButton
            active={tab === 'deposit'}
            onClick={() => handleTab('deposit')}
            icon={<ArrowDownCircle size={13} strokeWidth={2.2} />}
            label={t('wallet.tabDeposit')}
          />
          <TabButton
            active={tab === 'withdraw'}
            onClick={() => handleTab('withdraw')}
            icon={<ArrowUpCircle size={13} strokeWidth={2.2} />}
            label={t('wallet.tabWithdraw')}
          />
          <TabButton
            active={tab === 'p2p'}
            onClick={() => handleTab('p2p')}
            icon={<Send size={13} strokeWidth={2.2} />}
            label={t('wallet.tabP2P')}
          />
          <TabButton
            active={tab === 'history'}
            onClick={() => handleTab('history')}
            icon={<History size={13} strokeWidth={2.2} />}
            label={t('wallet.tabHistory')}
          />
        </div>
        <AnimatePresence mode="wait">
          {tab === 'deposit' && (
            <motion.div
              key="deposit"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22 }}
            >
              <DepositCard />
            </motion.div>
          )}
          {tab === 'withdraw' && (
            <motion.div
              key="withdraw"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22 }}
            >
              <WithdrawCard />
            </motion.div>
          )}
          {tab === 'history' && (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22 }}
            >
              <HistoryTab />
            </motion.div>
          )}
          {tab === 'p2p' && (
            <motion.div
              key="p2p"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22 }}
            >
              <P2PTab />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────
// TabButton
// ────────────────────────────────────────────────────────────────────
function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl py-2 px-3 flex items-center justify-center gap-1.5 transition-all active:scale-[0.97]"
      style={{
        background: active
          ? 'color-mix(in oklab, var(--accent-primary) 18%, transparent)'
          : 'transparent',
        border: active
          ? '1px solid color-mix(in oklab, var(--accent-primary) 40%, transparent)'
          : '1px solid transparent',
        color: active ? 'var(--accent-primary)' : 'var(--text-muted)',
        boxShadow: active
          ? '0 0 16px -4px color-mix(in oklab, var(--accent-primary) 55%, transparent)'
          : 'none',
      }}
    >
      {icon}
      <span className="text-[11px] font-mono font-extrabold uppercase tracking-[0.14em]">
        {label}
      </span>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────
// History tab — реальная история из dashboard.feed
// ────────────────────────────────────────────────────────────────────
type FeedItem = {
  kind: 'passive' | 'ref' | 'status' | 'spend' | 'topup';
  title: string;
  subtitle: string;
  amount: number;
};

const HISTORY_PAGE_SIZE = 20;

const HIDE_REF_INCOME_KEY = 'luna.wallet.hideRefIncome.v1';

function HistoryTab() {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Юзер может скрыть шум «доход от сети» (при сотне партнёров будет
  // много мелких записей). Выбор сохраняется в localStorage.
  const [hideRefIncome, setHideRefIncome] = useState<boolean>(() => {
    try { return localStorage.getItem(HIDE_REF_INCOME_KEY) === '1'; }
    catch { return false; }
  });

  const fetchPage = async (offset: number, append: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const { lunaApi } = await import('@/services/api');
      const hide = hideRefIncome ? ['referral_income'] : undefined;
      const r = await lunaApi.walletHistory.list(HISTORY_PAGE_SIZE, offset, hide);
      const data = (r as { data?: {
        items: FeedItem[]; total: number; filteredTotal?: number; hasMore: boolean;
      } })?.data;
      if (!data) throw new Error(t('wallet.history.emptyResponse'));
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      setTotal(data.filteredTotal ?? data.total);
      setHasMore(data.hasMore);
    } catch (e) {
      setError((e as Error).message ?? t('wallet.history.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPage(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideRefIncome]);

  // Слушаем 'luna:deposit-done' от кнопки «Пополнить» — сразу
  // освежаем первую страницу истории без полного reload.
  useEffect(() => {
    const onDepositDone = () => { void fetchPage(0, false); };
    window.addEventListener('luna:deposit-done', onDepositDone);
    return () => window.removeEventListener('luna:deposit-done', onDepositDone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideRefIncome]);

  const loadMore = () => {
    haptic.light();
    void fetchPage(items.length, true);
  };

  return (
    <ClickableCard className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
            {t('wallet.history.kicker')}
          </p>
          <h3 className="font-display text-[16px] font-extrabold text-[var(--text-primary)] tracking-tight">
            {t('wallet.history.title')}
          </h3>
        </div>
        <GlassPill tone="plasma" dot compact>
          {total}
        </GlassPill>
      </div>

      {/* Фильтр — одна кнопка скрыть/показать доход от сети.
          Полезно когда рефералов много и копейки спамят ленту. */}
      <button
        type="button"
        onClick={() => {
          haptic.light();
          setHideRefIncome((v) => {
            const next = !v;
            try { localStorage.setItem(HIDE_REF_INCOME_KEY, next ? '1' : '0'); }
            catch { /* ignore */ }
            return next;
          });
        }}
        className="mb-3 inline-flex items-center gap-1.5 rounded-full
                   px-2.5 py-1 transition active:scale-95"
        style={{
          background: hideRefIncome
            ? 'color-mix(in oklab, var(--accent-primary) 18%, transparent)'
            : 'var(--glass-fill)',
          color: hideRefIncome
            ? 'var(--accent-primary)'
            : 'var(--text-muted)',
          border: `1px solid ${hideRefIncome
            ? 'color-mix(in oklab, var(--accent-primary) 35%, transparent)'
            : 'var(--glass-stroke)'}`,
        }}
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.08em]">
          {hideRefIncome
            ? t('wallet.history.hideRefIncomeOn')
            : t('wallet.history.hideRefIncome')}
        </span>
      </button>

      {items.length === 0 && !loading ? (
        <p className="text-[12px] text-[var(--text-muted)] text-center py-6">
          {error ?? t('wallet.history.emptyAnswer')}
        </p>
      ) : (
        <>
          <ul className="space-y-2">
            {items.map((entry, i) => (
              <FeedRow key={i} entry={entry} />
            ))}
          </ul>

          {error && (
            <p className="text-[11px] text-[var(--status-negative)] text-center mt-3">
              {error}
            </p>
          )}

          {hasMore && (
            <button
              type="button"
              onClick={loadMore}
              disabled={loading}
              className="mt-4 w-full rounded-xl py-2.5 text-[12px] font-mono uppercase tracking-[0.14em] transition active:scale-[0.98] disabled:opacity-50"
              style={{
                background: 'var(--glass-fill)',
                border: '1px solid var(--glass-stroke)',
                color: 'var(--text-secondary)',
              }}
            >
              {loading
                ? t('wallet.history.loading')
                : t('wallet.history.showMore', { count: Math.min(HISTORY_PAGE_SIZE, total - items.length) })}
            </button>
          )}

          {!hasMore && items.length > HISTORY_PAGE_SIZE && (
            <p className="text-[10px] text-[var(--text-muted)] text-center mt-3 font-mono uppercase tracking-[0.14em]">
              {t('wallet.history.allShown')}
            </p>
          )}
        </>
      )}
    </ClickableCard>
  );
}

function FeedRow({ entry }: { entry: FeedItem }) {
  const positive = entry.amount >= 0;
  const color = positive ? 'var(--status-positive)' : 'var(--status-negative)';
  const tone = {
    passive: 'var(--accent-secondary)',
    ref: 'var(--accent-primary)',
    status: 'var(--accent-tertiary)',
    spend: 'var(--status-negative)',
    topup: 'var(--status-positive)',
  }[entry.kind];
  const Icon = {
    passive: Sparkles,
    ref: TrendingUp,
    status: Trophy,
    // Отправил/списал → стрелка ВВЕРХ (уход средств)
    spend: ArrowUpCircle,
    // Получил/пополнил → стрелка ВНИЗ (приход средств)
    topup: ArrowDownCircle,
  }[entry.kind];

  return (
    <li
      className="rounded-2xl px-3 py-2.5 flex items-center gap-3"
      style={{
        background: 'var(--glass-fill)',
        border: '1px solid var(--glass-stroke)',
      }}
    >
      <div
        className="h-9 w-9 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{
          background: `color-mix(in oklab, ${tone} 14%, transparent)`,
          border: `1px solid color-mix(in oklab, ${tone} 30%, transparent)`,
          color: tone,
        }}
      >
        <Icon size={14} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-[12px] font-semibold text-[var(--text-primary)] leading-tight"
          style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
        >
          {entry.title}
        </p>
        <p className="text-[10px] text-[var(--text-muted)] font-mono truncate mt-0.5">
          {entry.subtitle}
        </p>
      </div>
      <div className="text-right">
        <p
          className="font-mono text-[13px] font-extrabold tabular-nums"
          style={{ color }}
        >
          {positive ? '+' : ''}
          {parseFloat(entry.amount.toFixed(3))}{' '}
          <span className="text-[12px] text-[var(--text-muted)]">USD₮</span>
        </p>
      </div>
    </li>
  );
}


// ────────────────────────────────────────────────────────────────────
// P2P tab
// ────────────────────────────────────────────────────────────────────
// Шаги консольной анимации перевода — печатаются посимвольно
type P2PLevel = 'OK' | 'DBG' | 'INFO' | 'ERR';
const P2P_STEP_KEYS: Array<{ key: string; level: P2PLevel }> = [
  { key: 'wallet.p2p.step1', level: 'DBG' },
  { key: 'wallet.p2p.step2', level: 'INFO' },
  { key: 'wallet.p2p.step3', level: 'INFO' },
  { key: 'wallet.p2p.step4', level: 'DBG' },
  { key: 'wallet.p2p.step5', level: 'DBG' },
  { key: 'wallet.p2p.step6', level: 'INFO' },
  { key: 'wallet.p2p.step7', level: 'OK' },
];

const P2P_COLOR: Record<P2PLevel, string> = {
  OK:   'var(--status-positive)',
  INFO: 'var(--accent-secondary)',
  DBG:  'var(--text-muted)',
  ERR:  'var(--status-negative)',
};

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

interface P2PReceipt {
  txHash: string;
  target: string;
  amount: string;
  at: string;
}

function P2PTab() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const [target, setTarget] = useState('');
  const [amount, setAmount] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // Консоль: фиксированный контейнер + печать посимвольно.
  const [consoleVisible, setConsoleVisible] = useState(false);
  const [completedLines, setCompletedLines] = useState<
    Array<{ text: string; level: P2PLevel }>
  >([]);
  const [typingLine, setTypingLine] = useState<
    { text: string; level: P2PLevel; chars: number } | null
  >(null);
  const consoleRef = useRef<HTMLDivElement | null>(null);

  // Автоскрол консоли при добавлении строк
  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [completedLines, typingLine?.chars]);
  // Чек после успеха — живёт до reload страницы (никто не чистит)
  const [receipt, setReceipt] = useState<P2PReceipt | null>(null);

  const canSubmit = target.trim().length > 0 && Number(amount) >= 10 && !sending;

  const runConsoleTimeline = async (
    activeTarget: string,
    activeAmount: string,
    errRef: { current: Error | null },
  ): Promise<void> => {
    const typeLine = async (text: string, level: P2PLevel) => {
      for (let n = 0; n <= text.length; n++) {
        setTypingLine({ text, level, chars: n });
        await sleep(14);
      }
      await sleep(550);
      setCompletedLines((prev) => [...prev, { text, level }]);
      setTypingLine(null);
    };

    for (let i = 0; i < P2P_STEP_KEYS.length; i++) {
      // Если API уже вернул ошибку — выводим её строкой и выходим
      if (errRef.current) {
        const e = errRef.current as Error & {
          response?: { data?: { message?: string } };
        };
        const apiMsg = e.response?.data?.message || e.message || t('wallet.p2p.transferError');
        haptic.error();
        await typeLine(t('wallet.p2p.consoleError', { msg: apiMsg }), 'ERR');
        return;
      }

      const s = P2P_STEP_KEYS[i];
      const text = t(s.key, { target: activeTarget || '...', amt: activeAmount || '0' });

      if (s.level === 'OK') haptic.success();
      else if (s.level === 'INFO') haptic.light();

      await typeLine(text, s.level);

      if (i < P2P_STEP_KEYS.length - 1) await sleep(70);
    }
  };

  const handleSend = async () => {
    if (!canSubmit) return;
    haptic.medium();
    setSending(true);
    setMsg(null);
    setReceipt(null);
    setConsoleVisible(true);
    setCompletedLines([]);
    setTypingLine(null);

    const clean = target.trim().replace(/^@/, '');

    const errRef: { current: Error | null } = { current: null };

    try {
      const { lunaApi } = await import('@/services/api');
      const apiPromise = lunaApi.p2p.send(clean, amount).catch((e: Error) => {
        errRef.current = e;
        return null;
      });
      const consolePromise = runConsoleTimeline(clean, amount, errRef);
      const [apiRes] = await Promise.all([apiPromise, consolePromise]);

      if (errRef.current) throw errRef.current;

      const data = (apiRes as { data?: { txHash?: string } } | null)?.data;
      const txHash = data?.txHash ?? 'LN-UNKNOWN';

      setReceipt({
        txHash,
        target: /^\d+$/.test(clean) ? `TG ${clean}` : `@${clean}`,
        amount,
        at: new Date().toLocaleString(locale, {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        }),
      });
      haptic.success();
      uiSounds.p2pSend();
      setTarget('');
      setAmount('');
    } catch (err: unknown) {
      await new Promise((r) => setTimeout(r, 500));
      const axiosMsg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const errMsg = axiosMsg ?? (err as { message?: string })?.message ?? t('wallet.p2p.transferError');
      setMsg({ ok: false, text: errMsg });
      haptic.error();
      uiSounds.p2pError();
    } finally {
      // Консоль остаётся видной до reload страницы.
      setSending(false);
    }
  };

  return (
    <ClickableCard
      tone="plasma"
      className="p-5"
    >
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono mb-3">
          {t('wallet.p2p.kicker')}
        </p>

        <label className="block mb-3">
          <span className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-[0.14em] block mb-1">
            {t('wallet.p2p.receiver')}
          </span>
          <input
            type="text"
            value={target}
            // Разрешаем буквы, цифры, @ и _. Пробелы обрезаем.
            onChange={(e) => setTarget(e.target.value.replace(/[^A-Za-z0-9_@]/g, ''))}
            placeholder={t('wallet.p2p.receiverPh')}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            className="w-full rounded-xl px-3 py-2.5 text-[14px] font-mono text-[var(--text-primary)] bg-transparent outline-none focus:ring-0"
            style={{
              background: 'var(--glass-fill)',
              border: '1px solid var(--glass-stroke)',
            }}
          />
        </label>

        <label className="block mb-3">
          <span className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-[0.14em] block mb-1">
            {t('wallet.p2p.amountLabel')}
          </span>
          <input
            type="number"
            min={10}
            step={0.01}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="10.00"
            className="w-full rounded-xl px-3 py-2.5 text-[16px] font-display font-extrabold text-[var(--text-primary)] bg-transparent outline-none focus:ring-0 tabular-nums"
            style={{
              background: 'var(--glass-fill)',
              border: '1px solid var(--glass-stroke)',
            }}
          />
        </label>

        <GlassButton
          intent="primary"
          fullWidth
          onClick={handleSend}
          disabled={!canSubmit}
          trailing={<Send size={14} strokeWidth={2.4} />}
        >
          {sending ? t('wallet.p2p.sending') : t('wallet.p2p.sendBtn')}
        </GlassButton>

        {/* Консоль — фикс-высота, автоскрол, посимвольная печать */}
        {consoleVisible && (
          <div
            ref={consoleRef}
            className="mt-3 rounded-xl p-3 font-mono text-[10px] leading-[1.7] overflow-y-auto"
            style={{
              background: 'rgba(0,0,0,0.55)',
              border: '1px solid var(--glass-stroke)',
              height: 140,
              scrollbarWidth: 'thin',
            }}
          >
            {completedLines.map((s, i) => (
              <div key={i} style={{ color: P2P_COLOR[s.level] }}>
                {s.text}
              </div>
            ))}
            {typingLine && (
              <div style={{ color: P2P_COLOR[typingLine.level] }}>
                {typingLine.text.slice(0, typingLine.chars)}
                <motion.span
                  className="inline-block ml-0.5"
                  animate={{ opacity: [1, 1, 0, 0] }}
                  transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    times: [0, 0.5, 0.5, 1],
                  }}
                >
                  ▌
                </motion.span>
              </div>
            )}
          </div>
        )}

        {msg && !sending && !receipt && (
          <p
            className="text-[11px] font-mono mt-3 text-center"
            style={{
              color: msg.ok
                ? 'var(--status-positive)'
                : 'var(--status-negative)',
            }}
          >
            {msg.text}
          </p>
        )}

        {/* Чек успешного перевода — не исчезает до reload */}
        {receipt && !sending && (
          <div
            className="mt-3 rounded-2xl p-4 font-mono"
            style={{
              background: 'linear-gradient(180deg, rgba(34,197,94,0.10) 0%, rgba(34,197,94,0.04) 100%)',
              border: '1px solid color-mix(in oklab, var(--status-positive) 40%, transparent)',
              boxShadow: '0 4px 20px -4px color-mix(in oklab, var(--status-positive) 30%, transparent)',
            }}
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center"
                style={{
                  background: 'var(--status-positive)',
                  boxShadow: '0 0 16px color-mix(in oklab, var(--status-positive) 60%, transparent)',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3 7l3 3 5-6" stroke="#000" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span
                className="text-[12px] font-extrabold uppercase tracking-[0.16em]"
                style={{ color: 'var(--status-positive)' }}
              >
                {t('wallet.p2p.done')}
              </span>
            </div>

            <div className="space-y-1.5 text-[11px]">
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">{t('wallet.p2p.amount')}</span>
                <span className="text-[var(--text-primary)] font-bold tabular-nums">
                  {receipt.amount} USD₮
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">{t('wallet.p2p.receiverLabel')}</span>
                <span className="text-[var(--text-primary)] font-bold">
                  {receipt.target}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">{t('wallet.p2p.time')}</span>
                <span className="text-[var(--text-secondary)] tabular-nums">
                  {receipt.at}
                </span>
              </div>
              <div
                className="mt-2 pt-2 border-t"
                style={{ borderColor: 'color-mix(in oklab, var(--status-positive) 20%, transparent)' }}
              >
                <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)] mb-1">
                  {t('wallet.p2p.txHash')}
                </p>
                <p
                  className="text-[12px] font-bold text-center py-1.5 rounded"
                  style={{
                    color: 'var(--status-positive)',
                    background: 'rgba(0,0,0,0.35)',
                    letterSpacing: '0.06em',
                  }}
                >
                  {receipt.txHash}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </ClickableCard>
  );
}


